// Friends page (#/friends): friends with online status, requests, private
// messages, friendly battles and the block and mute list; and the player
// profile card (#/player/:name), reached from any trainer name in chat, on the
// leaderboards and in guild member lists. New requests, messages and battle
// invites arrive as hunt log lines and a count on the Friends link: nothing
// pops up and nothing stops the hunt. Rules come from shared/social.ts.
import { phoneSections } from './psec.ts';
import { SOCIAL, GIFT, giftAmount, giftCap, giftFee, giftNet, type FriendlySummary, type PmDTO, type ProfileDTO, type SocialDTO } from '../shared/social.ts';
import { type BattleResult, type TierId } from '../shared/arena.ts';
import { OUTFIT, outfitOf } from '../shared/outfits.ts';
import type { ClientMsg } from '../shared/protocol.ts';
import type { CardPrefs, SocialOp } from '../shared/social.ts';
import { S, serverNow } from './state.ts';
import { chatShown } from './chatfilter.ts';
import { spriteUrl } from './assets.ts';
import { esc, full, cname, left, ago } from './fmt.ts';
import { trainerFace } from './outfits.ts';
import { playerIcon } from './playericon.ts';
import { tierBadge, teamFace } from './arena.ts';
import { arenaOpensAt, leagueBadge } from './ranked.ts';
import type { LeagueId } from '../shared/ranked.ts';
import { tagChip } from './guild.ts';
import { logLine } from './hud.ts';
import { creatureGrade, auraTag } from './badges.ts';
import { setSnapshot } from './hovercard.ts';
import { confirmClick, isArmed } from './confirm.ts';
import { route, go } from './router.ts';
import { convShown, onDmNew } from './messages.ts';
import { battleLogHtml } from './battlelog.ts';
import { afterReplay, replayOver } from './boardview.ts';
import { toast } from './dialog.ts';
import { trainingPane } from './training.ts';
import { friendList } from './friends-list.ts';
import { synergyChips } from './synergy.ts';
import { PVP } from '../shared/pvp.ts';

export const F = {
  data: null as SocialDTO | null,
  profile: null as ProfileDTO | null,
  /** A profile asked for that doesn't exist (lower case). */
  missing: '',
  /** Conversations by friend name (lower case). */
  threads: new Map<string, PmDTO[]>(),
  /** When each conversation was last asked for (lower-case name), so one that never arrives offers a retry. */
  asked: new Map<string, number>(),
  /** Open battle logs by friendly battle id (null while loading). */
  logs: new Map<number, { side: 0 | 1; result: BattleResult; summary: FriendlySummary } | null>(),
  /** Battles that finished this session and haven't been watched to the end: their row keeps the winner back. */
  fresh: new Set<number>(),
  /** Notes this session: requests, answers, invites. Newest first. */
  notes: [] as { ts: number; text: string; kind: string }[],
  /** Profiles fetched this session (popup, hover preview, profile page), by lower-case name. missing: no such trainer. */
  cards: new Map<string, { at: number; p: ProfileDTO | null }>(),
  /** The trainer whose profile popup is open (lower case), or ''. */
  popup: '',
  /** Text being typed: the friend name box and the message box. */
  addName: '',
  draft: '',
  /** Coin gift form: the friend it is open for ('' = closed) and the amount being typed. */
  giftTo: '',
  giftAmt: '',
  /** The friend a gift was just sent to, until the server's confirmation shows on their row. */
  giftPending: '',
  giftDone: null as null | { name: string; text: string; until: number },
};

/** A gift over a tenth of the sender's coins takes a second click. */
export const giftNeedsConfirm = (amount: number, coins: number) => amount * 10 > coins;
export const giftConfirmKey = (name: string, amount: number) => `gift:${name.toLowerCase()}:${amount}`;

let send: (m: ClientMsg) => void = () => {};
let rerender: () => void = () => {};
export function bindSocial(o: { send: (m: ClientMsg) => void; render: () => void }) { send = o.send; rerender = o.render; }
const op = (o: SocialOp) => send({ t: 'social', op: o });

const lc = (s: string) => s.toLowerCase();
/** A trainer name as a link to their profile card. */
export const playerLink = (name: string, inner = esc(name)) => `<a class="plink" href="#/player/${encodeURIComponent(name)}" data-player="${esc(name)}">${inner}</a>`;

/** Chat lines from trainers you muted or blocked are hidden. */
export function chatHidden(name: string | undefined): boolean {
  if (!name || !F.data) return false;
  const n = lc(name);
  return F.data.blocked.some((b) => lc(b.name) === n);
}

/** What waits for you: friend requests, unread messages and battle invites. */
export function socialWaiting(): number {
  const d = F.data;
  if (!d) return 0;
  // Unread messages count on the Messages envelope instead (client/messages.ts dmWaiting).
  return d.incoming.length + d.invites.filter((i) => !i.mine).length;
}

// ---------------- messages in ----------------
export function loadSocial() { send({ t: 'social' }); }

/** Called when #/friends or #/player opens. */
export function openSocialRoute() {
  const r = route();
  if (r.page === 'player' && r.sub) op({ k: 'profile', name: r.sub });
  // Conversations live in chat (client/messages.ts); old links still land there.
  else if (r.page === 'friends' && r.sub === 'chat' && r.sub2) go(`#/chat/dm/${encodeURIComponent(r.sub2)}`, true);
  else loadSocial();
}

/** A conversation that hasn't arrived after this long shows a Try again button. */
export const THREAD_WAIT_MS = 6_000;
export function askThread(name: string) {
  F.asked.set(lc(name), Date.now());
  op({ k: 'pmThread', name });
  setTimeout(() => { if (!F.threads.has(lc(name))) rerender(); }, THREAD_WAIT_MS + 50);
}

export function onSocial(d: SocialDTO) { F.data = d; rerender(); onCard(F.popup); }
/** Called when a profile arrives (the popup and hover preview redraw from F.cards). */
let onCard: (name: string) => void = () => {};
export function bindCard(fn: (name: string) => void) { onCard = fn; }
/** Your showcase (up to SOCIAL.showcaseMax ids, in order) and companion (null clears it). */
export const sendShowcase = (ids: number[]) => op({ k: 'showcase', ids });
export const sendCompanion = (id: number | null) => op({ k: 'companion', id });
/** What your player card keeps from others. */
export const sendCard = (prefs: CardPrefs) => op({ k: 'card', prefs });
export const askProfile = (name: string, peek = false) => op({ k: 'profile', name, ...(peek ? { peek: true } : {}) });

export function onProfile(m: { p?: ProfileDTO; missing?: string }) {
  const name = lc(m.p?.name ?? m.missing ?? '');
  F.cards.set(name, { at: Date.now(), p: m.p ?? null });
  const r = route();
  if (r.page === 'player' && lc(r.sub) === name) {
    if (m.p) { F.profile = m.p; F.missing = ''; } else F.missing = name;
    rerender();
  }
  onCard(name);
}
export function onPmThread(m: { name: string; msgs: PmDTO[] }) { F.threads.set(lc(m.name), m.msgs); onDmNew(); rerender(); }

/** A message sent or received. One from someone else is a log line unless their conversation is open. */
export function onPmNew(msg: PmDTO) {
  const me = S.trainer?.name ?? '';
  const other = lc(msg.from) === lc(me) ? msg.to : msg.from;
  const list = F.threads.get(lc(other));
  if (list && !list.some((x) => x.id === msg.id)) list.push(msg);
  const r = route();
  if (lc(msg.from) !== lc(me)) {
    if (convShown(other)) askThread(other);
    else logLine(`Message from ${esc(msg.from)}: ${esc(chatShown(msg.text).slice(0, 80))} <a href="#/chat/dm/${encodeURIComponent(msg.from)}">Reply</a>`);
  }
  onDmNew();
  rerender();
}

export function onSocialNote(m: { text: string; kind: string }) {
  F.notes.unshift({ ts: Date.now(), text: m.text, kind: m.kind });
  if (F.notes.length > 30) F.notes.length = 30;
  const link = m.kind === 'invite' ? '#/friends/battles' : m.kind === 'friend' ? '#/friends/requests' : m.kind === 'gift' ? '#/trainer/friends' : '#/friends';
  logLine(`${esc(m.text)} <a href="${link}">Friends</a>`, m.kind === 'invite' || m.kind === 'gift' ? 'gold' : '');
  if (m.kind === 'gift' && F.giftPending && m.text.startsWith('You sent ')) {
    F.giftDone = { name: F.giftPending, text: m.text, until: Date.now() + 8000 };
    F.giftPending = '';
    setTimeout(rerender, 8100);
    rerender();
  }
}

/** Opens a friendly battle's board on the Friendly battles tab (its log loads from the server). */
function watchFriendly(id: number) {
  if (!F.logs.get(id)) { F.logs.set(id, null); op({ k: 'match', id }); }
  if (!(route().page === 'friends' && route().sub === 'battles')) go('#/friends/battles');
  rerender();
}

/** A friendly battle just finished. It never names the winner here: the fight plays on the board first. */
export function onFriendlyResult(m: FriendlySummary) {
  const me = S.trainer?.name ?? '';
  const opp = lc(m.names[0]) === lc(me) ? m.names[1] : m.names[0];
  F.fresh.add(m.id);
  const r = route();
  if (r.page === 'friends' && r.sub === 'battles') { watchFriendly(m.id); return; }
  logLine(`Friendly battle against ${esc(opp)} is done <a href="#/friends/battles">Watch it</a>`, 'gold');
  toast(`Your friendly battle against ${opp} is done`, 'gold', { label: 'Watch', run: () => watchFriendly(m.id) });
}
export function onFriendlyMatch(m: { id: number; side: 0 | 1; result: BattleResult; summary: FriendlySummary }) { F.logs.set(m.id, m); rerender(); }

// ---------------- page head ----------------
export function friendsHead() {
  const d = F.data;
  const sub = route().sub;
  const req = d?.incoming.length ?? 0;
  const inv = d?.invites.filter((i) => !i.mine).length ?? 0;
  const tab = (id: string, name: string, n = 0) => `<a href="#/friends${id ? `/${id}` : ''}" class="${sub === id || (id === '' && sub === 'chat') ? 'on' : ''}">${name}${n ? ` <span class="unread">${n}</span>` : ''}</a>`;
  const online = d?.friends.filter((f) => f.online).length ?? 0;
  return {
    meta: d ? `${full(d.friends.length)} ${d.friends.length === 1 ? 'friend' : 'friends'} · ${full(online)} online` : '',
    tabs: `<nav class="subtabs">${tab('', 'Friends')}${tab('requests', 'Requests', req)}${tab('battles', '<span class="st-long">Friendly battles</span><span class="st-short">Battles</span>', inv)}${tab('training', 'Training')}${tab('blocked', 'Blocked')}</nav>`,
    actions: '<button class="btn small" data-act="fr-refresh">Refresh</button>',
  };
}

export function friendsPane(): string {
  const r = route();
  if (r.sub === 'training') return trainingPane();
  const d = F.data;
  if (!d) return '<p class="hint">Loading…</p>';
  switch (r.sub) {
    case 'requests': return requestsHtml(d);
    case 'battles': return battlesHtml(d);
    case 'blocked': return blockedHtml(d);
  }
  return friendsHtml(d);
}

// ---------------- friends ----------------
function addForm() {
  return `<form class="fr-add" data-fr-form="add"><label class="sr" for="fr-add-name">Trainer name</label>
    <input id="fr-add-name" name="name" data-fr="add" maxlength="16" placeholder="Trainer name" value="${esc(F.addName)}" autocomplete="off">
    <button class="btn small primary">Send friend request</button></form>`;
}

/** Why the Challenge button is off, or '' when it works. */
function challengeBlock(d: SocialDTO, online: boolean): string {
  const now = serverNow();
  if (!online) return 'Offline';
  if (d.friendlyAt > now) return `Your next friendly battle can start in ${left(d.friendlyAt - now)}`;
  if (d.invites.some((i) => i.mine)) return 'Invite pending';
  return '';
}

function friendsHtml(d: SocialDTO) {
  return `${invitesHtml(d)}${friendList(d, {}, addForm())}${notesHtml()}`;
}

/** The latest notes this session (requests, answers, invites), as an inbox. */
function notesHtml() {
  if (!F.notes.length) return '';
  return `<section class="panel fr-inbox"><h4>Inbox</h4><ul>${F.notes.slice(0, 6).map((n) => `<li data-key="n${n.ts}-${esc(n.text.slice(0, 12))}"><span class="muted">${new Date(n.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span> ${esc(n.text)}</li>`).join('')}</ul></section>`;
}

function invitesHtml(d: SocialDTO) {
  if (!d.invites.length) return '';
  const now = serverNow();
  return `<section class="panel fr-invites"><h4>Friendly battle invites</h4><ul>${d.invites.map((i) => i.mine
    ? `<li data-key="i${i.id}"><span>Waiting for ${playerLink(i.to)} to answer · <span class="cd">${left(i.expires - now)}</span> left</span><button class="btn tiny" data-act="fr-inv-cancel" data-id="${i.id}">Cancel</button></li>`
    : `<li data-key="i${i.id}"><span>${playerLink(i.from)} challenges you · <span class="cd">${left(i.expires - now)}</span> left to answer</span><span><button class="btn tiny primary" data-act="fr-inv-yes" data-id="${i.id}">Accept and fight</button> <button class="btn tiny" data-act="fr-inv-no" data-id="${i.id}">Decline</button></span></li>`).join('')}</ul></section>`;
}

// ---------------- requests ----------------
function requestsHtml(d: SocialDTO) {
  const row = (x: SocialDTO['incoming'][number], incoming: boolean) => `<li data-key="r${x.id}"><span class="gname">${trainerFace(x.outfit)}${playerLink(x.name, `<b>${esc(x.name)}</b>`)} <span class="muted">Lv ${full(x.level)} · ${ago(x.at)} ago</span></span>
    <span>${incoming ? `<button class="btn tiny primary" data-act="fr-accept" data-id="${x.id}">Accept</button> <button class="btn tiny" data-act="fr-decline" data-id="${x.id}">Decline</button>` : `<button class="btn tiny" data-act="fr-cancel" data-id="${x.id}">Cancel</button>`}</span></li>`;
  return `${addForm()}
    <h4 class="sec">Waiting for you</h4>${d.incoming.length ? `<ul class="glines">${d.incoming.map((x) => row(x, true)).join('')}</ul>` : '<p class="hint">No requests waiting.</p>'}
    <h4 class="sec">Sent by you</h4>${d.outgoing.length ? `<ul class="glines">${d.outgoing.map((x) => row(x, false)).join('')}</ul>` : '<p class="hint">None waiting.</p>'}
    `;
}

// ---------------- friendly battles ----------------
function battlesHtml(d: SocialDTO) {
  const now = serverNow();
  const me = S.trainer?.name ?? '';
  const ready = d.friendlyAt <= now;
  const results = d.results.map((m) => {
    const mine: 0 | 1 = lc(m.names[0]) === lc(me) ? 0 : 1;
    const opp = m.names[1 - mine];
    const won = lc(m.winner) === lc(me);
    const open = F.logs.has(m.id);
    const x = F.logs.get(m.id);
    // The log first: it starts the replay, which decides whether a fresh battle's winner shows yet.
    const log = open ? (x ? battleLogHtml(x.result, x.side === 0 ? ['You', opp] : [opp, 'You'], x.side) : '<p class="hint">Loading the log…</p>') : '';
    const hold = F.fresh.has(m.id) && !(x && replayOver(x.result));
    const outcome = `<b class="${won ? 'good' : 'bad'}">${won ? 'Won' : 'Lost'}</b>`;
    const time = `<span class="muted">${(m.ms / 1000).toFixed(1)} s${m.timedOut ? ', time ran out' : ''}</span>`;
    return `<li class="${hold ? '' : won ? 'win' : 'loss'}" data-key="m${m.id}"><button class="ar-m" data-act="fr-log" data-id="${m.id}" aria-expanded="${open}">
      ${hold ? (x ? afterReplay(x.result, outcome, 'span') : '') : outcome}<span>vs ${esc(opp)}</span>${hold ? (x ? afterReplay(x.result, time, 'span') : '') : time}<span class="spacer"></span>
      <span class="muted">${ago(m.at)} ago · log kept ${left(m.keepUntil - now)} more</span></button>
      ${log}</li>`;
  }).join('');
  return `${invitesHtml(d)}
    <div class="panel fr-status"><b>${ready ? 'Ready' : `Next battle in ${left(d.friendlyAt - now)}`}</b>
      </div>
    <h4 class="sec">Last ${SOCIAL.resultKeepMs / 60_000} minutes</h4>
    ${results ? `<ul class="ar-recent">${results}</ul>` : '<p class="hint">No recent battles.</p>'}
    `;
}

// ---------------- blocked ----------------
export function blockedHtml(d: SocialDTO) {
  const rows = d.blocked.map((b) => `<li data-key="b-${esc(b.name)}"><span>${playerLink(b.name, `<b>${esc(b.name)}</b>`)} <span class="pill">${b.kind === 'block' ? 'Blocked' : 'Muted'}</span></span>
    <button class="btn tiny" data-act="fr-unblock" data-id="${esc(b.name)}" data-kind="${b.kind}">${b.kind === 'block' ? 'Unblock' : 'Unmute'}</button></li>`).join('');
  return `${rows ? `<ul class="glines">${rows}</ul>` : '<p class="hint">Nobody blocked or muted.</p>'}
    `;
}

// ---------------- profile ----------------
export function playerHead() {
  const p = F.profile;
  const name = route().sub;
  return { meta: p && lc(p.name) === lc(name) ? `Lv ${full(p.level)}${p.guild ? ` · ${esc(p.guild.name)}` : ''}` : '', actions: '<button class="btn small" data-act="fr-profile-refresh">Refresh</button>' };
}

export function playerPane(): string {
  const name = route().sub;
  if (!name) return '<p class="hint">No trainer picked.</p>';
  if (F.missing === lc(name)) return `<p class="hint">No trainer named ${esc(name)}.</p>`;
  const p = F.profile;
  if (!p || lc(p.name) !== lc(name)) return '<p class="hint">Loading the profile…</p>';
  const fit = outfitOf(p.outfit);
  const d = F.data;
  const arena = p.arena
    ? `${p.arena.ranked ? leagueBadge(p.arena.tierId as LeagueId) : tierBadge(p.arena.tierId as TierId, p.arena.tier)} <span><b>${full(p.arena.ar)}</b> ${p.arena.ranked ? 'trophies' : 'AR'} · ${p.arena.wins} won · ${p.arena.losses} lost this season</span>`
    : `<span class="muted">${p.level < arenaOpensAt() ? `Arena opens at Lv ${arenaOpensAt()}` : 'No ranked match this season'}</span>`;
  const show = p.showcase.length
    ? `<div class="fr-show">${p.showcase.map((c) => { setSnapshot(`pf-${c.id}`, c, p.name); return `<span class="show-chip" data-snap="pf-${c.id}" tabindex="0"><img src="${spriteUrl(c.species, c.rolls.aura)}" alt=""><span class="sc-t"><b>${esc(cname(c))}</b><span class="sc-l">${creatureGrade(c, 'sm')}${c.rolls.aura ? auraTag() : ''}<span class="muted">Lv ${full(c.level)}</span></span></span></span>`; }).join('')}</div>`
    : `<p class="hint">${p.relation === 'self' ? 'Empty' : 'Nothing on show.'}</p>`;
  return `<div class="pf">
    <section class="panel pf-hero"><div class="tp-sprite">${playerIcon({ outfit: p.outfit, comp: p.companion ? { species: p.companion.species, aura: p.companion.rolls.aura } : null, size: 'xl', name: `${p.name} in the ${OUTFIT[fit.id]?.name ?? fit.name}` })}</div>
      <div class="grow"><h2 style="color:${p.nameColor ?? 'inherit'}">${p.guild ? tagChip(p.guild.tag) : ''}${esc(p.name)}</h2>
        <div class="pf-line"><span class="lvl">Lv ${full(p.level)}</span>${p.online !== undefined ? `<span class="gname">${p.online ? '<i class="gdot"></i> Online' : '<i class="gdot off"></i> Offline'}</span>` : ''}</div>
        <div class="pf-line">${p.guild ? `Guild: <b>${esc(p.guild.name)}</b> [${esc(p.guild.tag)}]` : '<span class="muted">Not in a guild</span>'}</div>
        <div class="pf-line">${arena}</div>
        <div class="actions pf-acts">${actionsHtml(p, d)}</div>${p.relation === 'friend' && d && challengeBlock(d, !!p.online) ? `<p class="muted pf-why">${esc(challengeBlock(d, !!p.online))}.</p>` : ''}</div></section>
    ${phoneSections('pf', [
      { key: 'team', title: 'Arena line-up', html: `<section class="panel"><h4>Arena line-up</h4><div class="ar-team">${p.lineup.map((c) => teamFace(c)).join('') || '<span class="muted">No creatures</span>'}</div>${synergyChips(p.lineup)}
      </section>` },
      { key: 'show', title: 'Showcase', html: `<section class="panel"><div class="row between"><h4>Showcase</h4>${p.relation === 'self' ? '<a class="btn small" href="#/trainer/showcase">Choose creatures</a>' : ''}</div>${show}</section>` },
    ])}
  </div>`;
}

/** Friend, message, challenge, mute and block buttons for a profile (the page and the popup). */
export function actionsHtml(p: ProfileDTO, d: SocialDTO | null): string {
  if (p.relation === 'self') return '<a class="btn small" href="#/friends">Your friends</a>';
  const n = esc(p.name);
  const out: string[] = [];
  if (!p.blocked) out.push(`<button class="btn small primary" data-act="dm-open" data-id="${n}">Message</button>`);
  if (p.relation === 'friend') {
    const block = d ? challengeBlock(d, !!p.online) : '';
    out.push(`<button class="btn small" data-act="fr-invite" data-id="${n}"${block ? ` disabled title="${esc(block)}"` : ''}>Challenge</button>`);
    out.push(`<button class="btn small ${isArmed(`unfriend:${p.name}`) ? 'danger' : 'danger-outline'}" data-act="fr-remove" data-id="${n}">${isArmed(`unfriend:${p.name}`) ? 'Confirm remove' : 'Remove friend'}</button>`);
  } else if (p.relation === 'sent') out.push(`<span class="muted">Friend request sent</span><button class="btn small" data-act="fr-cancel" data-id="${p.requestId}">Cancel request</button>`);
  else if (p.relation === 'received') out.push(`<button class="btn small primary" data-act="fr-accept" data-id="${p.requestId}">Accept friend request</button><button class="btn small" data-act="fr-decline" data-id="${p.requestId}">Decline</button>`);
  else if (!p.blocked) out.push(`<button class="btn small primary" data-act="fr-add" data-id="${n}">Add friend</button>`);
  if (!p.blocked) out.push(`<button class="btn small" data-act="fr-mute" data-id="${n}" aria-pressed="${p.muted}">${p.muted ? 'Unmute' : 'Mute'}</button>`);
  out.push(p.blocked ? `<button class="btn small" data-act="fr-block" data-id="${n}" aria-pressed="true">Unblock</button>`
    : `<button class="btn small ${isArmed(`block:${p.name}`) ? 'danger' : 'danger-outline'}" data-act="fr-block" data-id="${n}" aria-pressed="false">${isArmed(`block:${p.name}`) ? 'Confirm block' : 'Block'}</button>`);
  return out.join('');
}

/** The fee and net line under the amount, also updated while typing. */
export function giftCalcText(raw: string): string {
  const d = F.data;
  const n = giftAmount(raw.replace(/[,\s]/g, ''));
  const leftToday = d ? Math.max(0, d.gift.cap - d.gift.sent) : 0;
  if (n === null) return `Whole coins, ${full(GIFT.minCoins)} or more · ${full(leftToday)} left today`;
  return `Fee ${GIFT.burnPct}% burned: ${full(giftFee(n))} · They get ${full(giftNet(n))} · ${full(leftToday)} left today`;
}

// ---------------- events ----------------
export function socialClick(el: HTMLElement): boolean {
  const act = el.dataset.act ?? '';
  const id = el.dataset.id ?? '';
  switch (act) {
    case 'fr-refresh': loadSocial(); return true;
    case 'fr-profile-refresh': openSocialRoute(); return true;
    case 'fr-thread-retry': askThread(id); rerender(); return true;
    case 'fr-add': op({ k: 'friendAdd', name: id }); refreshProfile(); return true;
    case 'fr-accept': op({ k: 'friendAnswer', id: Number(id), accept: true }); refreshProfile(); return true;
    case 'fr-decline': op({ k: 'friendAnswer', id: Number(id), accept: false }); refreshProfile(); return true;
    case 'fr-cancel': op({ k: 'friendCancel', id: Number(id) }); refreshProfile(); return true;
    case 'fr-remove': if (confirmClick(`unfriend:${id}`)) { op({ k: 'friendRemove', name: id }); refreshProfile(); } return true;
    case 'fr-invite': op({ k: 'invite', name: id }); return true;
    case 'fr-inv-yes': op({ k: 'inviteAnswer', id: Number(id), accept: true }); return true;
    case 'fr-inv-no': op({ k: 'inviteAnswer', id: Number(id), accept: false }); return true;
    case 'fr-inv-cancel': op({ k: 'inviteCancel', id: Number(id) }); return true;
    case 'fr-mute': { const p = F.cards.get(lc(id))?.p; op({ k: 'block', name: id, kind: 'mute', on: !p?.muted }); refreshProfile(); return true; }
    case 'fr-block': {
      const p = F.cards.get(lc(id))?.p;
      const blocked = !!p?.blocked;
      if (blocked) { op({ k: 'block', name: id, kind: 'block', on: false }); refreshProfile(); return true; }
      if (confirmClick(`block:${id}`)) { op({ k: 'block', name: id, kind: 'block', on: true }); refreshProfile(); }
      return true;
    }
    case 'fr-unblock': op({ k: 'block', name: id, kind: el.dataset.kind === 'mute' ? 'mute' : 'block', on: false }); return true;
    case 'fr-log': {
      const mid = Number(id);
      if (F.logs.has(mid)) F.logs.delete(mid); else { F.logs.set(mid, null); op({ k: 'match', id: mid }); }
      rerender(); return true;
    }
    case 'fr-gift': {
      // Toggle the compact Send coins form under that friend's row (one open at a time).
      const open = !!id && F.giftTo.toLowerCase() !== id.toLowerCase();
      F.giftTo = open ? id : ''; F.giftAmt = '';
      rerender();
      if (open) document.getElementById('cg-amt')?.focus();
      return true;
    }
    case 'fr-gift-max': {
      F.giftAmt = String(Math.max(0, Math.floor(Number(id)) || 0));
      const inp = document.getElementById('cg-amt') as HTMLInputElement | null;
      if (inp) { inp.value = F.giftAmt; socialInput(inp); }
      return true;
    }
  }
  return false;
}

function refreshProfile() {
  const r = route();
  if (r.page === 'player' && r.sub) op({ k: 'profile', name: r.sub });
  if (F.popup && !(r.page === 'player' && lc(r.sub) === F.popup)) op({ k: 'profile', name: F.popup });
}

/** Typing in the friend name or message box: kept so re-renders don't lose it. */
export function socialInput(el: HTMLInputElement | HTMLSelectElement): boolean {
  if (el.dataset.fr === 'add') { F.addName = el.value; return true; }
  if (el.dataset.fr === 'pm') { F.draft = el.value; return true; }
  if (el.dataset.fr === 'gift') {
    // Only the fee line changes while typing; a full re-render would move the caret.
    F.giftAmt = el.value;
    const out = el.closest('form')?.querySelector('.cg-calc');
    if (out) out.textContent = giftCalcText(el.value);
    return true;
  }
  return false;
}

export function socialSubmit(form: HTMLFormElement): boolean {
  const kind = form.dataset.frForm;
  if (!kind) return false;
  const data = new FormData(form);
  if (kind === 'add') {
    const name = String(data.get('name') ?? '').trim();
    if (name) op({ k: 'friendAdd', name });
    F.addName = '';
    (form.querySelector('input') as HTMLInputElement).value = '';
  } else if (kind === 'gift') {
    const amount = giftAmount(String(data.get('amount') ?? '').replace(/[,\s]/g, ''));
    const name = form.dataset.id ?? '';
    if (amount === null) { logLine(`Type a whole number of coins (at least ${full(GIFT.minCoins)}).`); return true; }
    if (giftNeedsConfirm(amount, S.trainer?.coins ?? 0) && !confirmClick(giftConfirmKey(name, amount))) return true;
    op({ k: 'gift', name, amount });
    F.giftTo = ''; F.giftAmt = ''; F.giftPending = name; F.giftDone = null;
  } else if (kind === 'pm') {
    const text = String(data.get('text') ?? '').trim();
    if (text) op({ k: 'pm', name: form.dataset.id ?? '', text });
    F.draft = '';
    (form.querySelector('input') as HTMLInputElement).value = '';
  }
  rerender();
  return true;
}

/** Help page section. Numbers come from shared/social.ts. */
export function socialHelp(): string {
  return `<h4>Friends</h4><p>The Friends page (in the app bar, or under More) lists your friends with their level and whether they are online. Click any trainer's name in chat, on the leaderboards or in a guild's member list to open their profile: level, outfit, guild tag, arena tier, the creatures in their arena line-up (not their order) and up to ${SOCIAL.showcaseMax} creatures they chose to show. From there you can send a friend request, mute or block.</p>
    <ul><li><b>Requests</b>: up to ${SOCIAL.outgoingMax} waiting at once; they lapse after ${SOCIAL.requestMs / 86_400_000} days. Up to ${SOCIAL.friendsMax} friends. Sending a request to someone who already sent you one makes you friends.</li>
      <li><b>Messages</b> go to any trainer: click a name and choose Message, open the envelope in the app bar, or type <code>/w name text</code> in chat. Up to ${SOCIAL.pmMaxLen} characters, with the same spam limits as chat. The last ${SOCIAL.pmKeep} per conversation are kept. A first message from someone who isn't a friend or guildmate waits under Requests until you answer. Starting a conversation with someone new needs Lv ${SOCIAL.dmMinLevel}, at most ${SOCIAL.dmNewPerHour} new trainers an hour. In Settings you choose who can message you: everyone, friends and guild, or friends only.</li>
      <li><b>Mute</b> hides someone's chat lines for you. <b>Block</b> also ends a friendship and stops requests, messages and battle invites between you. The other trainer is not told.</li></ul>
    <h4>Friendly battles</h4><p>Challenge an online friend from the Friends list or their profile. The battle uses the ranked arena's rules and engine (arena line-up, else hunt team; every creature at Lv ${PVP.fixedLevel}) and changes no rating and pays no prize. You can have one invite waiting at a time; it lapses after ${SOCIAL.inviteMs / 60_000} minutes. The invite shows as a line in the hunt log and on the Friends page, never as a pop-up. After a battle, both trainers wait ${SOCIAL.friendlyCooldownMs / 60_000} minutes before another friendly battle with anyone. The result and its log stay on the Friendly battles tab for ${SOCIAL.resultKeepMs / 60_000} minutes.</p>
    <h4>Coin gifts</h4><p>On the player screen's Friends tab, <b>Send coins</b> on a friend's row opens a small form: type the amount and it shows the fee and what your friend gets. ${GIFT.burnPct}% of every gift is burned (it goes to nobody), so a gift of ${full(10_000)} costs you ${full(10_000)} and your friend gets ${full(10_000 - giftFee(10_000))}. The smallest gift is ${full(GIFT.minCoins)} coins. Both trainers need Lv ${GIFT.minLevel}, and you must have been friends for ${GIFT.friendMs / 86_400_000} days (removing a friend starts the count again). Each day (UTC) you can send, and receive from everyone together, up to about an hour of hunting coins at your level (${full(giftCap(GIFT.minLevel))} at Lv ${GIFT.minLevel}, ${full(giftCap(500))} at Lv 500). Coins from admin commands can't be sent, and admin accounts can't send or receive. Your friend sees a line in the hunt log, never a pop-up. Only coins can be gifted: creatures and items go through the market.</p>
    <h4>Training area</h4><p>On the Friends page, Training sets up a battle of up to ${SOCIAL.trainingSide} creatures per side: copies of your own creatures, or made-up ones with any species, level, aura, Bloodline, quality, six talents, trait, stars and refine (made-up ones can carry the relic of one of your creatures). It uses the ranked rules and engine. Nothing is saved and nothing is paid; your creatures are not changed. One training battle every ${SOCIAL.trainingCooldownMs / 60_000} minutes per account. Use it to test a refine plan before spending star items.</p>`;
}

